/**
 * SummaryTab v2 — replaces the old SummaryTabNew layout with the locked
 * Deal Overview Mockup v1 design, but driven by REAL data from
 * GET /api/deals/{id}/summary.
 *
 * Strict data integrity rule (per user spec): every field either renders a
 * real value or a <SummaryWarning /> with the exact missing-field message
 * from the backend. No silent zero-fallbacks anywhere on this page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ExternalLink } from 'lucide-react';
import SummaryWarning from './SummaryWarning';
import { toast } from 'sonner';

import RemindersCard from '../reminders/RemindersCard';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 09/01 (Josh): "allow me to edit broker/iso, funder, and merchant in the
// case that i have to, from the actual deal page" — inline party editor:
// small Change link on each party column → contact search → PATCH the deal.
const PartyEdit = ({ label, fieldName, dealId, getAuthHeaders, onRefetch }) => {
  const [openEd, setOpenEd] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!openEd || q.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/contacts?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
        const list = Array.isArray(res.data) ? res.data : res.data.contacts || [];
        setResults(list.slice(0, 6));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, openEd, getAuthHeaders]);
  const pick = async (c) => {
    try {
      await axios.patch(`${API}/deals/${dealId}`, { [fieldName]: c.id }, { headers: getAuthHeaders() });
      toast.success(`${label} set to ${c.company_name || c.name}`);
      setOpenEd(false); setQ('');
      onRefetch?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to update ${label}`);
    }
  };
  if (!openEd) {
    return (
      <button type="button"
        className="text-[10px] text-blue-600 hover:underline ml-2"
        onClick={() => setOpenEd(true)}
        data-testid={`edit-party-${fieldName}`}>
        Change
      </button>
    );
  }
  return (
    <span className="relative inline-block ml-2 align-middle">
      <input autoFocus value={q} onChange={e => setQ(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { setOpenEd(false); setQ(''); } }}
        placeholder={`Search ${label.toLowerCase()}…`}
        className="h-6 w-44 rounded border border-slate-300 px-2 text-xs"
        data-testid={`edit-party-${fieldName}-input`} />
      <button type="button" className="text-[10px] text-slate-400 hover:text-slate-600 ml-1"
        onClick={() => { setOpenEd(false); setQ(''); }}>✕</button>
      {results.length > 0 && (
        <span className="absolute left-0 top-7 z-50 block w-56 rounded-md border border-slate-200 bg-white shadow-lg">
          {results.map(c => (
            <button key={c.id} type="button" onClick={() => pick(c)}
              className="block w-full truncate px-2 py-1.5 text-left text-xs hover:bg-slate-100">
              {c.company_name || c.name}
              {c.shortcode ? <span className="text-slate-400"> · {c.shortcode}</span> : null}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

// BL-025: terminal statuses for which Forecast Payoff Date is hidden.
// Mirror of backend/utils/deal_summary.py:TERMINAL_STATUSES_FOR_FORECAST.
const TERMINAL_STATUSES_FOR_FORECAST = new Set([
  'Paid in Full',
  'Paid in Full (Refinanced)',
  'Paid in Full / Settled',
  'Cancelled',
]);

/* ───────────────────────── helpers ─────────────────────────────────────── */

const fmt$ = (n) =>
  typeof n !== 'number' || isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmt$_paren = (n) => {
  if (typeof n !== 'number' || isNaN(n)) return '—';
  return n < 0 ? `(${fmt$(Math.abs(n))})` : fmt$(n);
};

const fmtPct = (n, digits = 1) =>
  typeof n !== 'number' || isNaN(n) ? '—' : `${n.toFixed(digits)}%`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
};

/** Render a result that's either {value:...} or {error, missing_field, fix_hint}.
 *  `renderValue` is invoked with `r.value` when present. */
const Result = ({ r, renderValue, onEditDeal, label }) => {
  if (!r) return <span className="text-slate-400">—</span>;
  if (r.error) {
    return (
      <SummaryWarning
        error={r.error}
        fixHint={r.fix_hint}
        label={label}
        missingField={r.missing_field}
        onEditDeal={onEditDeal}
      />
    );
  }
  // healthy null (e.g. "no bounces yet")
  if (r.healthy_null) {
    return <span className="text-slate-400 text-sm italic">None</span>;
  }
  return renderValue(r.value);
};

/* ───────────────────────── tone palette ────────────────────────────────── */

const TONE = {
  green:  { bg: 'bg-emerald-50',  border: 'border-emerald-200',  value: 'text-emerald-900', label: 'text-emerald-700', subBright: 'text-emerald-800' },
  red:    { bg: 'bg-rose-50',     border: 'border-rose-200',     value: 'text-rose-900',    label: 'text-rose-700',    subBright: 'text-rose-800' },
  blue:   { bg: 'bg-sky-50',      border: 'border-sky-200',      value: 'text-sky-900',     label: 'text-sky-700',     subBright: 'text-sky-800' },
};

/* ───────────────────────── row primitives ──────────────────────────────── */

const DetailRow = ({ label, children, accent = false }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-xs text-slate-500 font-medium">{label}</span>
    <span className={`text-sm tabular-nums text-right ${accent ? 'font-semibold text-slate-900' : 'text-slate-800'}`}>
      {children}
    </span>
  </div>
);

const ColumnHeading = ({ children }) => (
  <div className="mb-2.5 pb-1.5 border-b border-slate-200/70">
    <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{children}</h4>
  </div>
);

/* ───────────────────────── main component ──────────────────────────────── */

const SummaryTab = ({ deal, merchant, broker, funder, refinanceInfo, getAuthHeaders, onRefetch, isAdmin, onEditDeal }) => {
  const { user: authUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!deal?.deal_id) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/deals/${deal.deal_id}/summary`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deal?.deal_id, getAuthHeaders]);

  if (loading || !summary) {
    return (
      <div className="p-8 text-center text-sm text-slate-500" data-testid="summary-loading">
        Loading deal summary…
      </div>
    );
  }

  const { tiles, original, funding, post_funding, cadence, counts, parties } = summary;
  // Forward all args (notably missing_field) so the dispatch router can route.
  const edit = (missingField) => { if (onEditDeal) onEditDeal(missingField); };

  const hasRefiChain = !!(refinanceInfo && (refinanceInfo.refinanced_to || refinanceInfo.refinanced_from));

  return (
    <div className="space-y-4" data-testid="summary-tab-v2">

      {/* 1. KPI strip — 6 equal tiles */}
      <div className="flex gap-3">

        <KpiTile label="Last Payment" tone="green" isActivity result={tiles.last_payment}
          renderValue={(v) => fmtDate(v.date)}
          renderSub={(v) => fmt$(v.amount)}
          onEditDeal={edit} />

        <KpiTile label="Last Cleared" tone="green" isActivity result={tiles.last_cleared}
          renderValue={(v) => fmtDate(v.date)}
          renderSub={(v) => `${fmt$(v.amount)} ✓ cleared`}
          onEditDeal={edit} />

        <KpiTile label="Last Bounce" tone="red" isActivity result={tiles.last_bounce}
          renderValue={(v) => fmtDate(v.date)}
          renderSub={(v) => `${fmt$(v.amount)}${v.return_code ? ` · ${v.return_code}` : ''}`}
          onEditDeal={edit}
          healthyNullSub="No bounces" />

        <KpiTile label="Cash Exposure" tone="red" result={tiles.cash_exposure}
          renderValue={(v) => fmt$(v.amount)}
          renderSub={() => 'at funding'}
          onEditDeal={edit} />

        <KpiTile label="Net Revenue" tone="green" result={tiles.net_revenue}
          renderValue={(v) => fmt$(v.amount)}
          renderSub={(v) => v.basis || 'projected'}
          onEditDeal={edit} />

        <KpiTile label="% Repaid" tone="blue" result={tiles.percent_repaid}
          renderValue={(v) => fmtPct(v.pct)}
          renderSub={(v) => `of ${fmt$(v.payback)}`}
          onEditDeal={edit} />
      </div>

      {/* 2. Three-column detail card */}
      <Card className="p-5">
        <div className="grid grid-cols-3 divide-x divide-slate-200">

          <div className="pr-5">
            <ColumnHeading>Original Deal</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Advance Amount">
                <Result r={original.advance_amount} label="Advance Amount" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Factor">
                <Result r={original.factor} label="Factor" onEditDeal={edit}
                  renderValue={(v) => v.factor.toFixed(3)} />
              </DetailRow>
              <DetailRow label="Payback Amount">
                <Result r={original.payback} label="Payback Amount" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Origination Fees">
                <Result r={original.origination_fees} label="Origination Fees" onEditDeal={edit}
                  renderValue={(v) => `${fmt$(v.amount)} (${fmtPct(v.pct)})`} />
              </DetailRow>
              <DetailRow label="Term">
                <Result r={original.term} label="Term" onEditDeal={edit}
                  renderValue={(v) => `${v.days} days`} />
              </DetailRow>
              {refinanceInfo?.is_refinance && (refinanceInfo.refi_amount || 0) > 0 && (
                <DetailRow label="Refinanced Amount" accent>
                  {fmt$(refinanceInfo.refi_amount)}
                </DetailRow>
              )}
            </div>
          </div>

          <div className="px-5">
            <ColumnHeading>Actual Funding &amp; Collection</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Funded Date">
                <Result r={funding.funded_date} label="Funded Date" onEditDeal={edit}
                  renderValue={(v) => fmtDate(v.date)} />
              </DetailRow>
              <DetailRow label="Net Advance">
                <Result r={funding.net_advance} label="Net Advance" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Admin Fees">
                <Result r={funding.admin_fees} label="Admin Fees" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Revised Factor">
                <Result r={funding.revised_factor} label="Revised Factor" onEditDeal={edit}
                  renderValue={(v) => v.factor.toFixed(4)} />
              </DetailRow>
              <DetailRow label="Expected Payback">
                <Result r={funding.expected_payback} label="Expected Payback" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Daily Collection">
                <Result r={funding.daily_collection} label="Daily Collection" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Collection Start">
                <Result r={funding.collection_start} label="Collection Start" onEditDeal={edit}
                  renderValue={(v) => fmtDate(v.date)} />
              </DetailRow>
              <DetailRow label="Schedule">
                <Result r={funding.schedule} label="Schedule" onEditDeal={edit}
                  renderValue={(v) => v.label} />
              </DetailRow>
            </div>
          </div>

          <div className="pl-5">
            <ColumnHeading>Post Funding</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Scheduled Commission">
                <Result r={post_funding.scheduled_commission} label="Scheduled Commission" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Commission Paid">
                <Result r={post_funding.commission_paid} label="Commission Paid" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Expected Deal Revenue">
                <Result r={post_funding.expected_deal_revenue} label="Expected Deal Revenue" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Actual Expected Payback">
                <Result r={post_funding.actual_expected_payback} label="Actual Expected Payback" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Days Since Funded">
                <Result r={post_funding.days_since_funded} label="Days Since Funded" onEditDeal={edit}
                  renderValue={(v) => `${v.days}`} />
              </DetailRow>
            </div>
          </div>
        </div>
      </Card>

      {/* 3. Payment Details */}
      <Card className="p-5">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 pb-2 border-b border-slate-200">
          Payment Details
        </h3>
        <div className="grid grid-cols-3 divide-x divide-slate-200">

          <div className="pr-5">
            <ColumnHeading>Payment Activity</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Last Payment">
                <Result r={tiles.last_payment} label="Last Payment" onEditDeal={edit}
                  renderValue={(v) => `${fmtDate(v.date)} · ${fmt$(v.amount)}`} />
              </DetailRow>
              <DetailRow label="Last Successful Payment">
                <Result r={tiles.last_cleared} label="Last Cleared" onEditDeal={edit}
                  renderValue={(v) => `${fmtDate(v.date)} · ${fmt$(v.amount)} ✓ cleared`} />
              </DetailRow>
              <DetailRow label="Last Bounce">
                {tiles.last_bounce?.healthy_null
                  ? <span className="text-slate-400 italic text-sm">None</span>
                  : <Result r={tiles.last_bounce} label="Last Bounce" onEditDeal={edit}
                      renderValue={(v) => `${fmtDate(v.date)} · ${fmt$(v.amount)}${v.return_code ? ` · ${v.return_code}` : ''}`} />}
              </DetailRow>
            </div>
          </div>

          <div className="px-5">
            <ColumnHeading>Cadence</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Est. Daily Payment">
                <Result r={cadence.est_daily_payment} label="Est. Daily Payment" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Avg Daily Payment">
                <Result r={cadence.avg_daily_payment} label="Avg Daily Payment" onEditDeal={edit}
                  renderValue={(v) => fmt$(v.amount)} />
              </DetailRow>
              <DetailRow label="Repayment Rate" accent>
                <Result r={cadence.repayment_rate} label="Repayment Rate" onEditDeal={edit}
                  renderValue={(v) => fmtPct(v.pct, 2)} />
              </DetailRow>
            </div>
          </div>

          <div className="pl-5">
            <ColumnHeading>Counts &amp; Dates</ColumnHeading>
            <div className="divide-y divide-slate-100">
              <DetailRow label="Est. # of Payments">
                <Result r={counts.est_num_payments} label="Est. # of Payments" onEditDeal={edit}
                  renderValue={(v) => v.count} />
              </DetailRow>
              <DetailRow label="Payments Remaining">
                <Result r={counts.payments_remaining} label="Payments Remaining" onEditDeal={edit}
                  renderValue={(v) => v.display ?? v.count} />
              </DetailRow>
              <DetailRow label={<span>Expected Payoff Date <span className="text-[10px] font-normal text-slate-400">· contractual</span></span>}>
                <Result r={counts.expected_payoff_date} label="Expected Payoff Date" onEditDeal={edit}
                  renderValue={(v) => (v.estimated ? `${fmtDate(v.date)} · est.` : fmtDate(v.date))} />
              </DetailRow>
              {!TERMINAL_STATUSES_FOR_FORECAST.has((deal?.status || '').trim()) && (
                <DetailRow label={<span>Forecast Payoff Date <span className="text-[10px] font-normal text-slate-400">· current pace</span></span>}>
                  <Result r={counts.forecast_payoff_date} label="Forecast Payoff Date" onEditDeal={edit}
                    renderValue={(v) => v.display ?? fmtDate(v.date)} />
                </DetailRow>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* 4. Bottom row — refi chain + parties (50/50, parties full-width when no chain) */}
      <div className={`grid gap-4 ${hasRefiChain ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {hasRefiChain && (
          <Card className="p-4 bg-violet-50/60 border-violet-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-violet-500" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-violet-800">Refinance Chain</span>
              </div>
              <div className="flex flex-col gap-1 text-sm">
                {refinanceInfo.refinanced_to && (
                  <span className="flex items-center gap-1 text-violet-700">
                    Refinanced into:
                    <a href={`/deals/${refinanceInfo.refinanced_to.deal_number}`}
                       className="inline-flex items-center gap-1 font-semibold text-violet-900 hover:text-violet-700">
                      #{refinanceInfo.refinanced_to.deal_number}
                      {refinanceInfo.refinanced_to.merchant_name && (
                        <span className="text-violet-700 font-normal"> — {refinanceInfo.refinanced_to.merchant_name}</span>
                      )}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </span>
                )}
                {refinanceInfo.refinanced_from && (refinanceInfo.refinanced_from_list || [refinanceInfo.refinanced_from]).map((rf) => (
                  <span key={rf.deal_number} className="flex items-center gap-1 text-violet-700">
                    Refinanced from:
                    <a href={`/deals/${rf.deal_number}`}
                       className="inline-flex items-center gap-1 font-semibold text-violet-900 hover:text-violet-700">
                      #{rf.deal_number}
                      {rf.merchant_name && (
                        <span className="text-violet-700 font-normal"> — {rf.merchant_name}</span>
                      )}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </span>
                ))}
              </div>
            </div>
          </Card>
        )}

        <Card className="p-4">
          <div className="grid grid-cols-3 divide-x divide-slate-200">
            <div className="pr-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Merchant
                {isAdmin && <PartyEdit label="Merchant" fieldName="company_id" dealId={deal?.id} getAuthHeaders={getAuthHeaders} onRefetch={onRefetch} />}
              </div>
              {parties?.merchant_link?.error ? (
                <SummaryWarning
                  error={parties.merchant_link.error}
                  fixHint={parties.merchant_link.fix_hint}
                  label="Merchant"
                  missingField={parties.merchant_link.missing_field}
                  onEditDeal={edit}
                />
              ) : merchant ? (
                <>
                  <div className="text-sm font-semibold text-slate-900 leading-tight">{merchant.company_name || merchant.name || '—'}</div>
                  {merchant.contact_name && <div className="text-xs text-slate-600 mt-0.5">{merchant.contact_name}</div>}
                  {merchant.phone && <div className="text-xs text-slate-500 tabular-nums mt-0.5">{merchant.phone}</div>}
                </>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
            <div className="px-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Broker/ISO
                {isAdmin && <PartyEdit label="Broker/ISO" fieldName="broker_id" dealId={deal?.id} getAuthHeaders={getAuthHeaders} onRefetch={onRefetch} />}
              </div>
              {parties?.broker_link?.error ? (
                <SummaryWarning
                  error={parties.broker_link.error}
                  fixHint={parties.broker_link.fix_hint}
                  label="Broker/ISO"
                  missingField={parties.broker_link.missing_field}
                  onEditDeal={edit}
                />
              ) : broker ? (
                <>
                  <div className="text-sm font-semibold text-slate-900 leading-tight">{broker.company_name || broker.name}</div>
                  {broker.contact_name && <div className="text-xs text-slate-600 mt-0.5">{broker.contact_name}</div>}
                  {broker.phone && <div className="text-xs text-slate-500 tabular-nums mt-0.5">{broker.phone}</div>}
                </>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
            <div className="pl-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Funder
                {isAdmin && <PartyEdit label="Funder" fieldName="funder_id" dealId={deal?.id} getAuthHeaders={getAuthHeaders} onRefetch={onRefetch} />}
              </div>
              {parties?.funder_link?.error ? (
                <SummaryWarning
                  error={parties.funder_link.error}
                  fixHint={parties.funder_link.fix_hint}
                  label="Funder"
                  missingField={parties.funder_link.missing_field}
                  onEditDeal={edit}
                />
              ) : funder ? (
                <>
                  <div className="text-sm font-semibold text-slate-900 leading-tight">{funder.company_name || funder.name}</div>
                  {funder.contact_name && <div className="text-xs text-slate-600 mt-0.5">{funder.contact_name}</div>}
                  {funder.phone && <div className="text-xs text-slate-500 tabular-nums mt-0.5">{funder.phone}</div>}
                </>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Phase 3 (10/07/2026): follow-up reminders — owned by whoever sets them (no rep on deals). */}
      <RemindersCard deal={deal} getAuthHeaders={getAuthHeaders} isAdmin={isAdmin} currentUserId={authUser?.id} />


      {/* Deal Notes is rendered by DealDetail.js, below this Summary tab —
          left intentionally outside this component per user spec. */}
    </div>
  );
};

/* ───────────────────────── inline KPI tile ─────────────────────────────── */

const KpiTile = ({ label, tone, result, renderValue, renderSub, healthyNullSub, isActivity, onEditDeal }) => {
  const t = TONE[tone] || TONE.blue;
  const valueClass = isActivity ? 'text-xl' : 'text-2xl';

  let valueNode, subNode;

  if (!result) {
    valueNode = <span className="text-slate-400">—</span>;
    subNode = null;
  } else if (result.error) {
    valueNode = (
      <SummaryWarning
        error={result.error}
        fixHint={result.fix_hint}
        label={label}
        missingField={result.missing_field}
        onEditDeal={onEditDeal}
      />
    );
    subNode = null;
  } else if (result.healthy_null) {
    valueNode = <span className="text-slate-400 text-base italic">None</span>;
    subNode = healthyNullSub || null;
  } else {
    valueNode = renderValue(result.value);
    subNode = renderSub ? renderSub(result.value) : null;
  }

  return (
    <div className={`flex-1 min-w-0 rounded-xl border ${t.border} ${t.bg} px-4 py-3.5`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${t.label}`}>{label}</div>
      <div className={`mt-1.5 ${valueClass} font-bold tabular-nums ${t.value}`}>{valueNode}</div>
      {subNode && (
        <div className={`mt-0.5 text-[11px] font-medium ${t.subBright}`}>{subNode}</div>
      )}
    </div>
  );
};

export { SummaryTab };
export default SummaryTab;
